import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import {
    ELEVATED_SYSTEM_WALLET_KEY,
    USER_SETTINGS_KEY,
    UserSettings,
} from './user-settings-helper';

interface UserSettingsContextProviderProps {
    children: ReactNode;
}

interface UserSettingsContextType {
    getUserSettings: () => UserSettings;
    getElevatedSystemWallet: () => ElevatedSystemWallet | undefined;
    updateUserSettings: (partialData: any) => void;
    clearUserSettings: () => void;
}

export interface ElevatedSystemWallet {
    id: string;
    description: string;
    elevated: boolean;
}

export const UserSettingsContext = createContext<
    UserSettingsContextType | undefined
>(undefined);

const UserSettingsContextProvider = ({
    children,
}: UserSettingsContextProviderProps): React.JSX.Element => {
    const getUserSettings = (): UserSettings => {
        return (
            JSON.parse(localStorage.getItem(USER_SETTINGS_KEY) as string) ?? {}
        );
    };

    const getElevatedSystemWallet = (): ElevatedSystemWallet | undefined => {
        return getUserSettings()[ELEVATED_SYSTEM_WALLET_KEY];
    };

    const updateUserSettings = (partialData: any): void => {
        const updatedObject = { ...getUserSettings(), ...partialData };

        localStorage.setItem(USER_SETTINGS_KEY, JSON.stringify(updatedObject));
    };

    const clearUserSettings = (): void => {
        localStorage.removeItem(USER_SETTINGS_KEY);
    };

    const value = useMemo(
        () => ({
            getUserSettings,
            getElevatedSystemWallet,
            updateUserSettings,
            clearUserSettings,
        }),
        [] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return (
        <UserSettingsContext.Provider value={value}>
            {children}
        </UserSettingsContext.Provider>
    );
};

const useUserSettings = (): UserSettingsContextType => {
    const context = useContext(UserSettingsContext);
    if (!context) {
        throw new Error(
            'useUserSettings must be used within an UserSettingsProvider'
        );
    }
    return context;
};

export { UserSettingsContextProvider, useUserSettings };
